import {
  Avatar,
  Anchor,
  Box,
  Button,
  Card,
  Container,
  Divider,
  Drawer,
  Grid,
  Group,
  Loader,
  Modal,
  MultiSelect,
  Paper,
  ScrollArea,
  Stack,
  Switch,
  Text,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { IconAdjustments, IconArrowLeft, IconCalendar, IconFilterOff } from '@tabler/icons-react';
import { SearchInput } from '../../components/SearchInput';
import dayjs from 'dayjs';
import type { Dispatch } from 'react';
import { useMemo, useState } from 'react';
import type { MockProvider } from '../public/intake/mockProviders';
import { StepBooking } from '../public/intake/steps/StepBooking';
import type { IntakeAction, IntakeAnswers } from '../public/intake/types';
import { INITIAL_ANSWERS } from '../public/intake/types';
import { IntakeDataProvider, useIntakeDataContext } from '../public/intake/IntakeDataContext';

type GenderFilter = 'male' | 'female';

export interface ClientIntakeDirectoryPageProps {
  /**
   * Optional practice identifier for embedded/public directory links.
   * Currently unused with mock providers, but kept for future wiring.
   */
  practiceId?: string;
  /**
   * Sticky top offset (px) for the filter bar when booking flow is enabled.
   * Use 0 for iframe/standalone pages, and the header height for shell layouts.
   */
  stickyTop?: number;
  /**
   * Whether to show the "Enable Booking Flow" switch in the header.
   */
  showBookingFlowToggle?: boolean;
  /**
   * Initial state for booking flow; if toggle is hidden, this effectively locks the mode.
   */
  initialBookingFlowEnabled?: boolean;
}

function toggleExclusive<T>(current: T | null, next: T): T | null {
  return current === next ? null : next;
}

const INSURANCE_OPTIONS = [
  'Aetna',
  'Anthem Blue Cross',
  'Beacon',
  'BlueCross BlueShield',
  'Carelon',
  'Cigna',
  'Evernorth',
  'Kaiser Permanente',
  'Magellan Health',
  'Managed Health Network',
  'Molina Health',
  'Regence BlueCross Blue Shield',
  'Select Health',
  'TriWest',
  'United HealthCare',
  'Optum',
  'Health Net',
] as const;

function getProviderInsurances(provider: MockProvider): string[] {
  if (!provider.acceptsInsurance) return [];
  // Deterministic (but lightweight) mapping so filtering feels real with mock data.
  const seed = provider.id
    .split('')
    .map((c) => c.charCodeAt(0))
    .reduce((a, b) => a + b, 0);
  const primary = INSURANCE_OPTIONS[seed % INSURANCE_OPTIONS.length];
  const secondary = INSURANCE_OPTIONS[(seed + 5) % INSURANCE_OPTIONS.length];
  return primary === secondary ? [primary] : [primary, secondary];
}

function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .map((s) => s[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
}

function formatTimeLabel(time: string | null): string {
  if (!time) return '';
  const normalized = time.length === 5 ? `${time}:00` : time; // allow HH:mm or HH:mm:ss
  return dayjs(`1970-01-01T${normalized}`).format('h:mm A');
}

function matchesSearch(provider: MockProvider, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return (
    provider.name.toLowerCase().includes(q) ||
    provider.credentials.toLowerCase().includes(q) ||
    provider.location.toLowerCase().includes(q) ||
    provider.specialties.some((s) => s.toLowerCase().includes(q)) ||
    provider.languages.some((l) => l.toLowerCase().includes(q)) ||
    getProviderInsurances(provider).some((i) => i.toLowerCase().includes(q))
  );
}

function ProviderDirectoryCard({
  provider,
  onBook,
  onViewProfile,
  showBooking,
}: {
  provider: MockProvider;
  onBook: (provider: MockProvider) => void;
  onViewProfile: (provider: MockProvider) => void;
  showBooking: boolean;
}): JSX.Element {
  const [expanded, setExpanded] = useState(false);
  const combinedBio = `${provider.shortBio} ${provider.longBio}`.trim();

  return (
    <Card withBorder radius="md" padding="sm">
      <Group align="stretch" wrap="nowrap" gap="sm">
        <Box
          style={{
            width: 220,
            borderRadius: 'var(--mantine-radius-md)',
            overflow: 'hidden',
            background: 'var(--mantine-color-gray-1)',
            flexShrink: 0,
          }}
        >
          {provider.headshotUrl ? (
            <img
              src={provider.headshotUrl}
              alt={`${provider.name} photo`}
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <Group justify="center" align="center" h="100%">
              <Avatar size={96} radius="md" color="gold">
                {initials(provider.name)}
              </Avatar>
            </Group>
          )}
        </Box>

        <Stack gap="xs" style={{ flex: 1, minWidth: 0, padding: 6 }}>
          <Group justify="space-between" align="flex-start" wrap="nowrap" gap="sm">
            <Box style={{ minWidth: 0 }}>
              <Text fw={700} size="lg" c="dark.8" lineClamp={1}>
                {provider.name}
              </Text>
              <Text size="sm" c="dimmed" lineClamp={1}>
                Unify Mental Health
              </Text>
            </Box>

            <Group gap="sm" wrap="nowrap">
              <Button variant="outline" color="gray" onClick={() => onViewProfile(provider)}>
                View profile
              </Button>
              {showBooking && (
                <Button color="gold" onClick={() => onBook(provider)} rightSection={<IconCalendar size={16} />}>
                  Book a session
                </Button>
              )}
            </Group>
          </Group>

          <Text size="sm" c="dark.6" style={{ lineHeight: 1.65 }} lineClamp={expanded ? undefined : 7}>
            {combinedBio}{' '}
            <Anchor
              component="button"
              type="button"
              underline="always"
              c="gold.7"
              onClick={() => setExpanded((v) => !v)}
              style={{ fontWeight: 500 }}
            >
              {expanded ? 'Show less' : 'Read more'}
            </Anchor>
          </Text>
        </Stack>
      </Group>
    </Card>
  );
}

function ProviderProfileModal({
  opened,
  onClose,
  provider,
  showBooking,
  onBook,
}: {
  opened: boolean;
  onClose: () => void;
  provider: MockProvider | null;
  showBooking: boolean;
  onBook: (provider: MockProvider) => void;
}): JSX.Element {
  if (!provider) {
    return <Modal opened={opened} onClose={onClose} fullScreen title={<Text fw={700}>Provider</Text>} />;
  }

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      fullScreen
      withCloseButton={false}
      padding={0}
      styles={{
        body: { height: '100vh', display: 'flex', flexDirection: 'column' },
        content: { height: '100vh' },
      }}
    >
      {/* Sticky header */}
      <Paper
        withBorder
        radius={0}
        px="md"
        py="sm"
        style={{
          position: 'sticky',
          top: 0,
          zIndex: 1,
          background: 'var(--mantine-color-body)',
        }}
      >
        <Group justify="space-between" align="center" wrap="nowrap">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={onClose}>
            Back
          </Button>
          {showBooking && (
            <Button
              color="gold"
              rightSection={<IconCalendar size={16} />}
              onClick={() => {
                onClose();
                onBook(provider);
              }}
            >
              Book a session
            </Button>
          )}
        </Group>
      </Paper>

      <ScrollArea style={{ flex: 1 }}>
        <Container size="md" py="xl">
          <Stack gap="lg">
            <Group align="flex-start" wrap="nowrap" gap="lg">
              <Box
                style={{
                  width: 220,
                  height: 260,
                  borderRadius: 'var(--mantine-radius-md)',
                  overflow: 'hidden',
                  background: 'var(--mantine-color-gray-1)',
                  flexShrink: 0,
                }}
              >
                {provider.headshotUrl ? (
                  <img
                    src={provider.headshotUrl}
                    alt={`${provider.name} photo`}
                    style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                  />
                ) : (
                  <Group justify="center" align="center" h="100%">
                    <Avatar size={120} radius="md" color="gold">
                      {initials(provider.name)}
                    </Avatar>
                  </Group>
                )}
              </Box>

              <Stack gap={6} style={{ flex: 1, minWidth: 0 }}>
                <Title order={2}>{provider.name}</Title>
                <Text c="dimmed">{provider.credentials}</Text>
                <Text c="dimmed" size="sm">
                  {provider.location}
                </Text>
                <Divider my="xs" />
                <Text fw={600}>About</Text>
                <Text c="dark.6" style={{ lineHeight: 1.7 }}>
                  {provider.longBio}
                </Text>
              </Stack>
            </Group>

            <Divider />

            <Grid gutter="lg">
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text fw={600} mb="xs">
                  Specialties
                </Text>
                <Stack gap={4}>
                  {provider.specialties.map((s) => (
                    <Text key={s} size="sm" c="dark.6">
                      {s}
                    </Text>
                  ))}
                </Stack>
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <Text fw={600} mb="xs">
                  Languages
                </Text>
                <Stack gap={4}>
                  {provider.languages.map((l) => (
                    <Text key={l} size="sm" c="dark.6">
                      {l}
                    </Text>
                  ))}
                </Stack>
              </Grid.Col>
            </Grid>
          </Stack>
        </Container>
      </ScrollArea>
    </Modal>
  );
}

export function ClientIntakeDirectoryPage(props: ClientIntakeDirectoryPageProps): JSX.Element {
  return (
    <IntakeDataProvider prefetchProviders>
      <ClientIntakeDirectoryPageInner {...props} />
    </IntakeDataProvider>
  );
}

function ClientIntakeDirectoryPageInner({
  practiceId: _practiceId,
  stickyTop = 0,
  showBookingFlowToggle = true,
  initialBookingFlowEnabled = true,
}: ClientIntakeDirectoryPageProps): JSX.Element {
  const { providers: allProviders, providersLoading } = useIntakeDataContext();
  const [bookingFlowEnabled, setBookingFlowEnabled] = useState(initialBookingFlowEnabled);
  const [search, setSearch] = useState('');
  const [specialties, setSpecialties] = useState<string[]>([]);
  const [languages, setLanguages] = useState<string[]>([]);
  const [insurances, setInsurances] = useState<string[]>([]);
  const [gender, setGender] = useState<GenderFilter | null>(null);
  const [sessionType, setSessionType] = useState<'in-person' | 'virtual' | null>(null);
  const [drawerOpened, setDrawerOpened] = useState(false);

  const [bookingModalOpened, setBookingModalOpened] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<MockProvider | null>(null);
  const [bookingAnswers, setBookingAnswers] = useState<IntakeAnswers>(() => ({ ...INITIAL_ANSWERS }));
  const [profileModalOpened, setProfileModalOpened] = useState(false);
  const [profileProvider, setProfileProvider] = useState<MockProvider | null>(null);

  const specialtyOptions = useMemo(() => {
    const values = new Set<string>();
    for (const p of allProviders) {
      for (const s of p.specialties) values.add(s);
    }
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [allProviders]);

  const languageOptions = useMemo(() => {
    const values = new Set<string>();
    for (const p of allProviders) {
      for (const l of p.languages) values.add(l);
    }
    return Array.from(values)
      .sort((a, b) => a.localeCompare(b))
      .map((value) => ({ value, label: value }));
  }, [allProviders]);

  const insuranceOptions = useMemo(() => {
    return INSURANCE_OPTIONS.map((value) => ({ value, label: value }));
  }, []);

  const filteredProviders = useMemo(() => {
    if (!bookingFlowEnabled) {
      return [...allProviders].sort((a, b) => a.name.localeCompare(b.name));
    }

    return allProviders.filter((p) => {
      if (!matchesSearch(p, search)) return false;

      // Quick buttons override Format select when used
      if (sessionType && !p.format.includes(sessionType)) return false;

      if (specialties.length > 0 && !specialties.some((s) => p.specialties.includes(s))) return false;
      if (languages.length > 0 && !languages.some((l) => p.languages.includes(l))) return false;
      if (gender && p.gender !== gender) return false;
      if (insurances.length > 0) {
        const providerInsurances = getProviderInsurances(p);
        if (!insurances.some((i) => providerInsurances.includes(i))) return false;
      }

      return true;
    }).sort((a, b) => a.name.localeCompare(b.name));
  }, [allProviders, bookingFlowEnabled, gender, insurances, languages, search, sessionType, specialties]);

  const bookingDispatch: Dispatch<IntakeAction> = (action) => {
    switch (action.type) {
      case 'SET_BOOKING_DATE':
        setBookingAnswers((prev) => ({ ...prev, bookingDate: action.payload }));
        return;
      case 'SET_BOOKING_TIME':
        setBookingAnswers((prev) => ({ ...prev, bookingTime: action.payload }));
        return;
      default:
        return;
    }
  };

  const openBooking = (provider: MockProvider): void => {
    if (!bookingFlowEnabled) return;
    setSelectedProvider(provider);
    setBookingAnswers({ ...INITIAL_ANSWERS });
    setBookingModalOpened(true);
  };

  const closeBooking = (): void => {
    setBookingModalOpened(false);
  };

  const openProfile = (provider: MockProvider): void => {
    setProfileProvider(provider);
    setProfileModalOpened(true);
  };

  const closeProfile = (): void => {
    setProfileModalOpened(false);
  };

  const clearFilters = (): void => {
    setSearch('');
    setSpecialties([]);
    setLanguages([]);
    setInsurances([]);
    setGender(null);
    setSessionType(null);
  };

  const canConfirmBooking = Boolean(selectedProvider && bookingAnswers.bookingDate && bookingAnswers.bookingTime);

  const additionalFiltersCount = specialties.length + languages.length + insurances.length;

  const { isMock, submit } = useIntakeDataContext();

  const handleConfirmBooking = (): void => {
    if (!selectedProvider) return;
    if (!bookingAnswers.bookingDate || !bookingAnswers.bookingTime) return;

    if (!isMock) {
      const submissionAnswers: IntakeAnswers = {
        ...bookingAnswers,
        selectedProviderId: selectedProvider.id,
        bookings: [
          {
            therapyType: bookingAnswers.therapyType ?? 'individual',
            sessionType: bookingAnswers.sessionType ?? 'intake',
            selectedProviderId: selectedProvider.id,
            bookingDate: bookingAnswers.bookingDate,
            bookingTime: bookingAnswers.bookingTime,
            cancelled: false,
          },
        ],
      };
      submit(submissionAnswers).catch((err) =>
        console.error('[Directory] booking submission error:', err),
      );
    }

    notifications.show({
      title: 'Booking requested',
      message: `Requested intake with ${selectedProvider.name} on ${dayjs(bookingAnswers.bookingDate).format(
        'MMM D, YYYY'
      )} at ${formatTimeLabel(bookingAnswers.bookingTime)}.`,
      color: 'green',
    });

    closeBooking();
  };

  const providersSummary = insurances.length > 0 ? `accepting ${insurances[0]}` : '';

  return (
    <Box py="xl">
      <Container size="xl">
        <Stack gap="md">
          {/* Self-contained "directory header" for iframe use */}
          <Group justify="space-between" align="center" wrap="wrap">
            <Title order={2}>Find a provider</Title>
            {showBookingFlowToggle && (
              <Switch
                checked={bookingFlowEnabled}
                label="Enable Booking Flow"
                onChange={(e) => {
                  const enabled = e.currentTarget.checked;
                  setBookingFlowEnabled(enabled);
                  if (!enabled) {
                    clearFilters();
                    setDrawerOpened(false);
                    setBookingModalOpened(false);
                  }
                }}
              />
            )}
          </Group>

          {bookingFlowEnabled && (
            <Paper
              withBorder
              radius="md"
              p="sm"
              style={{
                position: 'sticky',
                top: stickyTop,
                zIndex: 10,
                background: 'var(--mantine-color-body)',
              }}
            >
              <Group justify="space-between" align="center" wrap="wrap">
                <Group gap="xs">
                  <SearchInput
                    w={320}
                    placeholder="Search for a provider"
                    value={search}
                    onChange={(e) => setSearch(e.currentTarget.value)}
                  />
                </Group>

                <Group gap="xs" wrap="wrap">
                  <Button
                    variant={gender === 'male' ? 'filled' : 'default'}
                    color={gender === 'male' ? 'gold' : 'gray'}
                    onClick={() => setGender((v) => toggleExclusive(v, 'male'))}
                  >
                    Male
                  </Button>
                  <Button
                    variant={gender === 'female' ? 'filled' : 'default'}
                    color={gender === 'female' ? 'gold' : 'gray'}
                    onClick={() => setGender((v) => toggleExclusive(v, 'female'))}
                  >
                    Female
                  </Button>
                  <Divider orientation="vertical" />
                  <Button
                    variant={sessionType === 'in-person' ? 'filled' : 'default'}
                    color={sessionType === 'in-person' ? 'gold' : 'gray'}
                    onClick={() => setSessionType((v) => toggleExclusive(v, 'in-person'))}
                  >
                    In person
                  </Button>
                  <Button
                    variant={sessionType === 'virtual' ? 'filled' : 'default'}
                    color={sessionType === 'virtual' ? 'gold' : 'gray'}
                    onClick={() => setSessionType((v) => toggleExclusive(v, 'virtual'))}
                  >
                    Online
                  </Button>
                  <Divider orientation="vertical" />
                  <Button
                    variant="default"
                    leftSection={<IconAdjustments size={16} />}
                    onClick={() => setDrawerOpened(true)}
                  >
                    Additional Filters{additionalFiltersCount > 0 ? ` (${additionalFiltersCount})` : ''}
                  </Button>
                </Group>
              </Group>
            </Paper>
          )}

          <Grid gutter="lg">
            {/* Results column */}
            <Grid.Col span={{ base: 12, md: 12 }}>
              <Group justify="space-between" align="center" mb="sm">
                <Text fw={700} size="lg">
                  {bookingFlowEnabled ? (
                    <>
                      {filteredProviders.length} Providers {providersSummary}
                    </>
                  ) : (
                    <>Providers</>
                  )}
                </Text>
              </Group>

              {providersLoading ? (
                <Stack align="center" py="xl">
                  <Loader size="md" color="gold" />
                  <Text size="sm" c="dimmed">Loading providers…</Text>
                </Stack>
              ) : filteredProviders.length === 0 ? (
                <Card withBorder radius="md" padding="xl">
                  <Text c="dimmed" ta="center">
                    No providers match your filters. Try clearing filters or broadening your search.
                  </Text>
                </Card>
              ) : (
                <Stack gap="lg">
                  {filteredProviders.map((p) => (
                    <ProviderDirectoryCard
                      key={p.id}
                      provider={p}
                      onBook={openBooking}
                      onViewProfile={openProfile}
                      showBooking={bookingFlowEnabled}
                    />
                  ))}
                </Stack>
              )}
            </Grid.Col>
          </Grid>
        </Stack>
      </Container>

      {bookingFlowEnabled && (
        <Drawer
          opened={drawerOpened}
          onClose={() => setDrawerOpened(false)}
          position="right"
          title={<Text fw={700}>Additional filters</Text>}
          size="md"
          overlayProps={{ opacity: 0.3, blur: 2 }}
        >
          <Stack gap="md">
            <MultiSelect
              label="Specialties"
              placeholder="Choose…"
              data={specialtyOptions}
              value={specialties}
              onChange={setSpecialties}
              searchable
              clearable
            />

            <MultiSelect
              label="Languages"
              placeholder="Choose…"
              data={languageOptions}
              value={languages}
              onChange={setLanguages}
              searchable
              clearable
            />

            <Divider />

            <MultiSelect
              label="Insurance plans"
              placeholder="Choose…"
              data={insuranceOptions}
              value={insurances}
              onChange={setInsurances}
              searchable
              clearable
            />

            <Group justify="space-between" mt="sm">
              <Button variant="default" onClick={() => setDrawerOpened(false)}>
                Done
              </Button>
              <Button variant="subtle" leftSection={<IconFilterOff size={16} />} onClick={clearFilters}>
                Clear all
              </Button>
            </Group>
          </Stack>
        </Drawer>
      )}

      {bookingFlowEnabled && (
        <Modal
          opened={bookingModalOpened}
          onClose={closeBooking}
          title={<Text fw={700}>Book intake</Text>}
          size="lg"
          radius="md"
        >
          <Stack gap="md">
            {selectedProvider && (
              <Card withBorder radius="md" padding="md">
                <Group wrap="nowrap" gap="md">
                  <Avatar src={selectedProvider.headshotUrl ?? undefined} size={80} radius="md" color="gold">
                    {initials(selectedProvider.name)}
                  </Avatar>
                  <Box style={{ minWidth: 0 }}>
                    <Text fw={700} lineClamp={1}>
                      {selectedProvider.name}
                    </Text>
                    <Text size="sm" c="dimmed" lineClamp={1}>
                      {selectedProvider.credentials} • {selectedProvider.location}
                    </Text>
                  </Box>
                </Group>
              </Card>
            )}

            <StepBooking answers={bookingAnswers} dispatch={bookingDispatch} />

            <Group justify="flex-end">
              <Button variant="default" onClick={closeBooking}>
                Cancel
              </Button>
              <Button color="gold" onClick={handleConfirmBooking} disabled={!canConfirmBooking}>
                Confirm booking
              </Button>
            </Group>
          </Stack>
        </Modal>
      )}

      <ProviderProfileModal
        opened={profileModalOpened}
        onClose={closeProfile}
        provider={profileProvider}
        showBooking={bookingFlowEnabled}
        onBook={openBooking}
      />
    </Box>
  );
}

